(() => {
  'use strict';

  const VERSION = '4.1.47';
  const EVENT_NAME_PATTERN = /^ln_(finder|product|bundle|cart|lead|content|error)_[a-z0-9_]+$/;
  const BLOCKED_KEY_PATTERN = /^(email|phone|first_name|last_name|full_name|address|address1|address2|city|zip|postal_code|customer|customer_id|customer_tags|note|notes)$/i;
  const MAX_QUEUE_SIZE = 100;
  const MAX_STRING_LENGTH = 180;
  const MAX_ARRAY_LENGTH = 20;
  const MAX_DEPTH = 4;

  if (window.LashNovaAnalytics?.version === VERSION) return;

  const onceKeys = new Set();
  const queue = [];
  let publishRetryCount = 0;
  let publishRetryTimer = null;
  let impressionObserver = null;

  const parseContext = () => {
    try {
      const node = document.getElementById('LashNovaAnalyticsContext');
      const parsed = node ? JSON.parse(node.textContent || '{}') : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_error) {
      return {};
    }
  };

  const context = parseContext();

  const cleanString = (value) => String(value).trim().slice(0, MAX_STRING_LENGTH);

  const cleanValue = (value, depth = 0) => {
    if (depth > MAX_DEPTH || value === null || typeof value === 'undefined') return undefined;
    if (typeof value === 'string') return cleanString(value);
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;

    if (Array.isArray(value)) {
      return value
        .slice(0, MAX_ARRAY_LENGTH)
        .map((item) => cleanValue(item, depth + 1))
        .filter((item) => typeof item !== 'undefined');
    }

    if (typeof value === 'object') {
      return Object.entries(value).reduce((result, [key, item]) => {
        if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(key) || BLOCKED_KEY_PATTERN.test(key)) return result;
        const cleaned = cleanValue(item, depth + 1);
        if (typeof cleaned !== 'undefined' && cleaned !== '') result[key] = cleaned;
        return result;
      }, {});
    }

    return undefined;
  };

  const deviceType = () => {
    const width = window.innerWidth || document.documentElement.clientWidth || 0;
    if (width < 750) return 'mobile';
    if (width < 990) return 'tablet';
    return 'desktop';
  };

  // Preview controls can remove their query/iframe; retain a detected signal for this document only.
  let previewModeSeen = false;
  const isPreviewMode = () => {
    previewModeSeen = previewModeSeen || Boolean(
      window.Shopify?.designMode ||
      window.Shopify?.visualPreviewMode ||
      new URLSearchParams(window.location.search).has('preview_theme_id') ||
      new URLSearchParams(window.location.search).get('pb') === '0' ||
      window.location.hostname.toLowerCase().endsWith('.shopifypreview.com') ||
      document.getElementById('PBarNextFrame')
    );
    return previewModeSeen;
  };
  isPreviewMode();

  const basePayload = () => ({
    ...context,
    analytics_version: VERSION,
    device_type: deviceType(),
    page_path: window.location.pathname,
    preview_mode: isPreviewMode(),
  });

  const schedulePublishRetry = () => {
    if (publishRetryTimer || publishRetryCount >= 20 || queue.length === 0) return;
    publishRetryTimer = window.setTimeout(() => {
      publishRetryTimer = null;
      publishRetryCount += 1;
      flushQueue();
    }, Math.min(250 * publishRetryCount + 250, 2000));
  };

  const publish = (name, payload) => {
    const publisher = window.Shopify?.analytics?.publish;
    if (typeof publisher !== 'function') {
      if (queue.length < MAX_QUEUE_SIZE) queue.push({ name, payload });
      schedulePublishRetry();
      return;
    }

    try {
      Promise.resolve(publisher(name, {
        ...payload,
        preview_mode: payload.preview_mode === true || isPreviewMode(),
      })).catch(() => {});
    } catch (_error) {
      // Analytics must never interrupt shopping behavior.
    }
  };

  function flushQueue() {
    const publisher = window.Shopify?.analytics?.publish;
    if (typeof publisher !== 'function') {
      schedulePublishRetry();
      return;
    }

    publishRetryCount = 0;
    const pending = queue.splice(0, queue.length);
    pending.forEach(({ name, payload }) => publish(name, payload));
  }

  const track = (name, data = {}, options = {}) => {
    try {
      if (!EVENT_NAME_PATTERN.test(name)) return false;

      const onceKey = options?.onceKey ? cleanString(options.onceKey) : '';
      if (onceKey && onceKeys.has(onceKey)) return false;
      if (onceKey) onceKeys.add(onceKey);

      const payload = cleanValue({ ...basePayload(), ...data });
      if (!payload) return false;

      try {
        window.dispatchEvent(
          new CustomEvent('lashnova:analytics', {
            detail: { name, customData: payload },
          })
        );
      } catch (_error) {
        // CustomEvent is a QA aid only and must never block publishing.
      }

      publish(name, payload);
      return true;
    } catch (_error) {
      return false;
    }
  };

  const trackOnce = (name, data = {}, onceKey = name) => track(name, data, { onceKey });

  const classifyError = (error) => {
    const text = cleanString(error?.message || error || '').toLowerCase();
    if (/sold out|unavailable|inventory/.test(text)) return 'inventory_unavailable';
    if (/network|failed to fetch|load failed|offline/.test(text)) return 'network_error';
    if (/timeout|timed out/.test(text)) return 'timeout';
    if (/json|parse|unexpected token/.test(text)) return 'invalid_response';
    if (/variant|not ready|missing/.test(text)) return 'invalid_product_state';
    return 'cart_update_failed';
  };

  const boolValue = (value) => {
    if (value === true || value === 'true' || value === '1') return true;
    if (value === false || value === 'false' || value === '0') return false;
    return undefined;
  };

  const numberValue = (value) => {
    if (value === '' || value === null || typeof value === 'undefined') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const dataFromElement = (element) => {
    if (!(element instanceof Element)) return {};
    const dataset = element.dataset;
    const source = {
      surface: dataset.lnSurface,
      label: dataset.lnLabel,
      position: numberValue(dataset.lnPosition),
      product_id: numberValue(dataset.lnProductId),
      product_handle: dataset.lnProductHandle,
      product_title: dataset.lnProductTitle,
      variant_id: numberValue(dataset.lnVariantId),
      bundle_id: numberValue(dataset.lnBundleId),
      bundle_handle: dataset.lnBundleHandle,
      bundle_title: dataset.lnBundleTitle,
      bundle_type: dataset.lnBundleType,
      current_style: dataset.lnCurrentStyle,
      pairing: dataset.lnPairing,
      replace_count: numberValue(dataset.lnReplaceCount),
      upgrade_delta: numberValue(dataset.lnUpgradeDelta),
      cart_value_before: numberValue(dataset.lnCartValueBefore),
      projected_total: numberValue(dataset.lnProjectedTotal),
      unlocks_free_shipping: boolValue(dataset.lnUnlocksFreeShipping),
      component_one: dataset.lnComponentOne,
      component_two: dataset.lnComponentTwo,
      destination_path: dataset.lnDestination,
      available: boolValue(dataset.lnAvailable),
      value: numberValue(dataset.lnValue),
      currency: dataset.lnCurrency,
    };

    return Object.entries(source).reduce((result, [key, value]) => {
      if (typeof value !== 'undefined' && value !== '') result[key] = value;
      return result;
    }, {});
  };

  const nearestDataRoot = (element) =>
    element.closest?.(
      '[data-ln-track-event], [data-ln-product-card], [data-ln-bundle-card], [data-ln-impression-event]'
    );

  const destinationPath = (element) => {
    const link = element.closest?.('a[href]');
    if (!link) return undefined;
    try {
      return new URL(link.href, window.location.origin).pathname;
    } catch (_error) {
      return undefined;
    }
  };

  const isStorefrontNavigation = (element, pathPattern) => {
    const link = element.closest?.('a[href]');
    if (!link) return false;
    try {
      const url = new URL(link.href, window.location.origin);
      const isSamePageAnchor = url.href.includes('#') &&
        url.pathname === window.location.pathname && url.search === window.location.search;
      return url.origin === window.location.origin && pathPattern.test(url.pathname) && !isSamePageAnchor;
    } catch (_error) {
      return false;
    }
  };

  const handleTrackedClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const tracked = target.closest('[data-ln-track-event]');
    if (tracked) {
      const dataRoot = nearestDataRoot(tracked.parentElement) || tracked;
      track(tracked.dataset.lnTrackEvent, {
        ...dataFromElement(dataRoot),
        ...dataFromElement(tracked),
        destination_path: dataFromElement(tracked).destination_path || destinationPath(target),
      });
      return;
    }

    const productCard = target.closest('[data-ln-product-card]');
    if (productCard && target.closest('a[href], button')) {
      track('ln_product_card_click', {
        ...dataFromElement(productCard),
        destination_path: destinationPath(target),
      });
      return;
    }

    const bundleCard = target.closest('[data-ln-bundle-card]');
    if (bundleCard && target.closest('a[href], button')) {
      track('ln_bundle_card_click', {
        ...dataFromElement(bundleCard),
        destination_path: destinationPath(target),
      });
      return;
    }

    const checkout = target.closest('button[name="checkout"], [href*="/checkout"]');
    if (checkout) {
      track('ln_cart_checkout_click', {
        surface: checkout.closest('cart-drawer') ? 'cart_drawer' : 'cart_page',
      });
      return;
    }

    const singlePdpRecommendation = target.closest('.lnp-recommend-grid a[href*="/products/"]');
    if (singlePdpRecommendation) {
      const path = destinationPath(singlePdpRecommendation) || '';
      track('ln_product_recommendation_click', {
        surface: 'single_pdp_related',
        product_handle: path.split('/products/')[1]?.split('/')[0],
        product_title: singlePdpRecommendation.querySelector('h3')?.textContent?.trim(),
        destination_path: path,
      });
      return;
    }

    const bundlePdpRecommendation = target.closest('.lnb-related-grid a[href*="/products/"]');
    if (bundlePdpRecommendation) {
      const path = destinationPath(bundlePdpRecommendation) || '';
      track('ln_bundle_card_click', {
        surface: 'bundle_pdp_related',
        bundle_handle: path.split('/products/')[1]?.split('/')[0],
        bundle_title: bundlePdpRecommendation.querySelector('h3')?.textContent?.trim(),
        destination_path: path,
      });
      return;
    }

    const finderLink = target.closest('a[href*="/pages/ai-lash-finder"]');
    if (finderLink && isStorefrontNavigation(finderLink, /\/pages\/ai-lash-finder\/?$/)) {
      track('ln_finder_entry_click', {
        surface: context.page_type === 'article' && finderLink.closest('main')
          ? 'article'
          : finderLink.closest('header') ? 'header' : 'site_navigation',
        destination_path: destinationPath(finderLink),
        label: finderLink.textContent?.trim(),
      });
      return;
    }

    if (context.page_type === 'collection' || context.page_type === 'search') {
      const productLink = target.closest('a[href*="/products/"]');
      if (productLink && productLink.closest('main') && isStorefrontNavigation(productLink, /\/products\//)) {
        const path = destinationPath(productLink) || '';
        track('ln_product_card_click', {
          surface: context.page_type === 'search' ? 'search_results' : 'collection_grid',
          product_handle: path.split('/products/')[1]?.split('/')[0],
          destination_path: path,
          label: productLink.textContent?.trim(),
        });
        return;
      }
    }

    if (context.page_type === 'article') {
      const commerceLink = target.closest(
        'a[href*="/products/"], a[href*="/collections/"], a[href*="/pages/ai-lash-finder"]'
      );
      if (commerceLink && commerceLink.closest('main') && isStorefrontNavigation(commerceLink, /\/(?:products|collections)\/|\/pages\/ai-lash-finder\/?$/)) {
        track('ln_content_commerce_click', {
          surface: 'article',
          destination_path: destinationPath(commerceLink),
          label: commerceLink.textContent?.trim(),
        });
      }
    }
  };

  const observeImpression = (element) => {
    if (!(element instanceof Element) || element.dataset.lnImpressionReady === 'true') return;
    const name = element.dataset.lnImpressionEvent;
    if (!EVENT_NAME_PATTERN.test(name || '')) return;
    element.dataset.lnImpressionReady = 'true';

    const fire = () =>
      trackOnce(
        name,
        dataFromElement(element),
        `impression:${name}:${element.dataset.lnImpressionKey || element.id || JSON.stringify(dataFromElement(element))}`
      );

    if (!('IntersectionObserver' in window)) {
      fire();
      return;
    }

    if (!impressionObserver) {
      impressionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.35) return;
            const node = entry.target;
            impressionObserver.unobserve(node);
            const eventName = node.dataset.lnImpressionEvent;
            trackOnce(
              eventName,
              dataFromElement(node),
              `impression:${eventName}:${node.dataset.lnImpressionKey || node.id || JSON.stringify(dataFromElement(node))}`
            );
          });
        },
        { threshold: [0.35] }
      );
    }

    impressionObserver.observe(element);
  };

  const scan = (scope = document) => {
    if (scope instanceof Element && scope.matches('[data-ln-impression-event]')) observeImpression(scope);
    scope.querySelectorAll?.('[data-ln-impression-event]').forEach(observeImpression);

    const resultNodes = [];
    if (scope instanceof Element && scope.matches('[data-ln-form-result]')) resultNodes.push(scope);
    scope.querySelectorAll?.('[data-ln-form-result]').forEach((node) => resultNodes.push(node));
    resultNodes.forEach((node) => {
      const result = node.dataset.lnFormResult;
      const source = node.dataset.lnFormSource || 'unknown';
      const eventName = node.dataset.lnFormEvent || `ln_lead_${source}_${result}`;
      trackOnce(eventName, { surface: source, result }, `form:${eventName}:${source}:${window.location.pathname}`);
    });
  };

  const api = Object.freeze({
    version: VERSION,
    track,
    trackOnce,
    classifyError,
    flush: flushQueue,
    scan,
  });

  window.LashNovaAnalytics = api;
  document.addEventListener('click', handleTrackedClick, true);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => scan(node)));
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });

  flushQueue();
})();
